import { Suspense, lazy } from 'react';
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';

import Features from '@src/features.config';

import { ComponentLoading } from '@components/Loading';

import Layout from '@helper/layout';
import RouterDefaultLayout from '@helper/layout/RouterDefaultLayout';
import MinimalLayout from '@helper/layout/minimal';

import InternationalAccessEdit from '@screens/admin/international-access/editTiles';
import InternationalAccess from '@screens/admin/international-access/tilesIndex';
import MockPaytoBanking from '@screens/mock-payto-banking';

import RouteRequireAdminTileAccess from './PageRequireAdminTileAccess';
import RouteRequireOu from './PageRequireOu';
import RouteRequireAuth from './RouteRequireAuth';

const Home = lazy(() => import('@screens/home/index'));
const InvoicePayment = lazy(() => import('@screens/invoice-payment'));
const Donations = lazy(() => import('@screens/donations'));
const DonationsContactUs = lazy(() => import('@screens/donations-contact-us'));
const DonationsFaqs = lazy(() => import('@screens/donations-faqs'));
const InvoiceContactUs = lazy(() => import('@screens/invoice-payment-contact-us'));
const Faqs = lazy(() => import('@screens/faqs'));
const InvoiceFaqs = lazy(() => import('@screens/invoice-faqs'));
const PageNotFound = lazy(() => import('@screens/page-not-found'));
const PaytoAgreement = lazy(() => import('@screens/payto-agreement'));
const PageNotAuthorized = lazy(() => import('@screens/page-not-authorized'));
const Enable = lazy(() => import('@screens/enable'));
const EnableFaqs = lazy(() => import('@screens/enable-faqs'));
const MiscPayment = lazy(() => import('@screens/misc-payment'));
const MiscPaymentFaqs = lazy(() => import('@screens/misc-payment-faqs'));
const MiscPaymentContactForm = lazy(() => import('@screens/misc-payment-contact-form'));
const StaffPayment = lazy(() => import('@screens/staff-payment'));
const NSWMap = lazy(() => import('@screens/map'));
const InvoiceExceptionHandling = lazy(() => import('@screens/invoice-exception-handling'));

const MockCheckoutScript = lazy(() => import('@screens/mock-checkout-script'));

const Admin = lazy(() => import('@screens/admin'));
const AdminDynamicForms = lazy(() => import('@screens/admin/dynamic-forms'));
const AdminDynamicFormsEdit = lazy(() => import('@screens/admin/dynamic-forms/edit-form'));
const AdminDynamicFormsAdd = lazy(() => import('@screens/admin/dynamic-forms/add-form'));

const AdminOutage = lazy(() => import('@screens/admin/outage'));
const AdminOutageEditor = lazy(() => import('@screens/admin/outage/edit'));

const AdminCashierAccess = lazy(() => import('@screens/admin/cashier-access/index'));
const AdminCashierAccessEditor = lazy(() => import('@screens/admin/cashier-access/edit'));

const AdminRating = lazy(() => import('@screens/admin/rating-report'));

const QRCodeGeneration = lazy(() => import('@screens/admin/qr-code-generation/index'));
const CashierReport = lazy(() => import('@screens/cashier-report'));

const ReprintReceipt = lazy(() => import('@screens/reprint-receipt'));

const UserSettings = lazy(() => import('@screens/user-settings'));
const UserTerminalSelection = lazy(() => import('@screens/user-settings/terminal'));
const UserTerminalsManager = lazy(() => import('@screens/admin/terminals-manager'));
const UserTerminalsForm = lazy(() => import('@screens/admin/terminals-manager/addOrEdit'));

const POSScreenPairing = lazy(() => import('@screens/pos/POSPairing'));
const POSScreenTransaction = lazy(() => import('@screens/pos/POSTransaction'));

const HtppTransactionDashboard = lazy(
  () => import('@screens/admin/third-party/transaction-dashboard'),
);

// const Welcome = lazy(() => import('@screens/welcome'));
const Accommodation = lazy(() => import('@screens/accommodation'));
// const Education = lazy(() => import('@screens/education'));
// const Cremation = lazy(() => import('@screens/cremation'));
// const MedicalRecords = lazy(() => import('@screens/medical-records'));
// const MedicalRecordsRequestViewer = lazy(() => import('@screens/medical-records/request-viewer'));

// const AdminMedicalRecordFees = lazy(() => import('@screens/admin/medical-records/fees'));
// const AdminMedicalRecordsFulfiller = lazy(() => import('@screens/admin/medical-records/fulfiller'));
// const AdminMedicalRecords = lazy(() => import('@screens/admin/medical-records'));
// const AdminMedicalRecordViewRequest = lazy(
//   () => import('@screens/admin/medical-records/fulfiller/pages/viewRequest'),
// );
// const AdminOrganisations = lazy(() => import('@screens/admin/organisations'));
// const AdminAddHealthEntity = lazy(() => import('@screens/admin/organisations/health-entities/add'));
// const AdminEditHealthEntity = lazy(
//   () => import('@screens/admin/organisations/health-entities/edit'),
// );
// const AdminHealthEntities = lazy(() => import('@screens/admin/organisations/health-entities'));
// const AdminAddSite = lazy(() => import('@screens/admin/organisations/sites/addSite'));
// const AdminEditSite = lazy(() => import('@screens/admin/organisations/sites/editSite'));
// const AdminUtilities = lazy(() => import('@screens/admin/utilities'));

export default function AppRouter(props) {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* A catch-all route to display the not-found page */}
        <Route
          path="*"
          element={
            <Layout>
              <Navigate to="/not-found" replace={true} />
            </Layout>
          }
        />

        <Route
          path="/not-authorised"
          element={
            <Layout>
              <PageNotAuthorized />
            </Layout>
          }
        />
        <Route
          path="/not-found"
          element={
            <Layout>
              <PageNotFound />
            </Layout>
          }
        />

        <Route
          path="/payto-agreement"
          element={
            <Layout>
              <PaytoAgreement />
            </Layout>
          }
        />

        {/* All normal pages should be put within this Route so that the default layout is applied */}
        <Route
          path="/"
          element={<RouterDefaultLayout />}
          handle={{
            crumb: () => ({ to: '/', name: 'Home' }),
          }}
        >
          <Route index element={<Home />} />

          <Route
            path="/faqs"
            element={<Faqs />}
            handle={{
              crumb: () => ({ to: 'faqs', name: 'Frequently Asked Questions' }),
            }}
          />

          <Route
            path="/invoice"
            handle={{
              crumb: () => ({ to: 'invoice', name: 'Invoice Payment' }),
            }}
          >
            <Route index element={<InvoicePayment />} />

            <Route
              path="contact-us"
              element={<InvoiceContactUs />}
              handle={{
                crumb: () => ({ to: 'contact-us', name: 'Contact Us' }),
              }}
            />
            <Route
              path="faqs"
              element={<InvoiceFaqs />}
              handle={{
                crumb: () => ({ to: 'faqs', name: 'Frequently Asked Questions' }),
              }}
            />
            {/* <Route
              path="exception-handling"
              element={<InvoiceExceptionHandling />}
              handle={{
                crumb: () => ({ to: 'exception-handling', name: 'Correct Error' }),
              }}
            /> */}
          </Route>

          <Route
            path="error-correction"
            element={<InvoiceExceptionHandling />}
            handle={{
              crumb: () => ({ to: 'error-correction', name: 'Error Correction' }),
            }}
          />

          <Route
            path="/donations"
            handle={{
              crumb: () => ({ to: 'donations', name: 'Donations' }),
            }}
          >
            <Route index element={<Donations />} />

            <Route
              path="contact-us"
              element={<DonationsContactUs />}
              handle={{
                crumb: () => ({ to: 'contact-us', name: 'Contact Us' }),
              }}
            />
            <Route
              path="faqs"
              element={<DonationsFaqs />}
              handle={{
                crumb: () => ({ to: 'faqs', name: 'Frequently Asked Questions' }),
              }}
            />
          </Route>

          <Route
            path="/other-payments"
            handle={{
              crumb: () => ({ to: 'other-payments', name: 'Non-Invoice Payment' }),
            }}
          >
            <Route index element={<MiscPayment />} />

            <Route
              path="contact-us"
              element={<MiscPaymentContactForm />}
              handle={{
                crumb: () => ({ to: 'contact-us', name: 'Contact Us' }),
              }}
            />
            <Route
              path="faqs"
              element={<MiscPaymentFaqs />}
              handle={{
                crumb: () => ({ to: 'faqs', name: 'Frequently Asked Questions' }),
              }}
            />
          </Route>

          <Route
            path="/enablensw"
            element={<RouteRequireOu roles={['cashier']} healthEntity={'HS'} />}
            handle={{
              crumb: () => ({ to: 'enablensw', name: 'EnableNSW' }),
            }}
          >
            <Route index element={<Enable />} />

            <Route
              path="faqs"
              element={<EnableFaqs />}
              handle={{
                crumb: () => ({ to: 'faqs', name: 'Frequently Asked Questions' }),
              }}
            />
          </Route>

          <Route
            path="/enablensw"
            handle={{
              crumb: () => ({ to: 'enablensw', name: 'EnableNSW' }),
            }}
          ></Route>

          {Features.services.staffPayments && (
            <Route
              path="/staff-payment"
              handle={{
                crumb: () => ({ to: 'staff-payment', name: 'Staff Payment' }),
              }}
            >
              <Route index element={<StaffPayment />} />
            </Route>
          )}

          {Features.admin.cashierReport && (
            <Route
              path="/cashier/cashier-report"
              element={<RouteRequireAuth roles={['cashier']} />}
              handle={{
                crumb: () => ({ to: 'cashier-report', name: 'Cashier' }),
              }}
            >
              <Route index element={<CashierReport />} />)
            </Route>
          )}

          {Features.admin.reprintReceipt && (
            <Route
              path="/cashier/reprint-receipt"
              element={<RouteRequireAuth roles={['cashier']} />}
              handle={{
                crumb: () => ({ to: 'reprint-receipt', name: 'Reprint Receipt' }),
              }}
            >
              <Route index element={<ReprintReceipt />} />)
            </Route>
          )}

          {Features.services.nswHealthMap && <Route path="/map" element={<NSWMap />} />}

          {/* {Features.services.medicalRecords && (
          <>
            <Route path={'/medical-records'}>
              <Route index element={<MedicalRecords />} />

              <Route path={'apply/:formStep?'} element={<MedicalRecords />} />
              <Route path={'view-request/:id'} element={<MedicalRecordsRequestViewer />} />
            </Route>
          </>
        )} */}

          {/* <Route path="/rating-report" element={<RatingReport />} /> */}
          {/* <Route path="/welcome" element={<Welcome />} /> */}

          {Features.services.accommodation && (
            <>
              <Route path="/accommodation" element={<Accommodation />} />
              <Route path="/accommodation/new/:id" element={<Accommodation />} />
              <Route path="/accommodation/new/:id/review" element={<Accommodation />} />
              <Route path="/accommodation/:action" element={<Accommodation />} />
            </>
          )}
          {/* 
        {Features.services.cremation && <Route path="/cremation" element={<Cremation />} />}
        {Features.services.education && <Route path="/education" element={<Education />} />} */}

          {Features.userSettings && (
            <Route
              path="/user-settings"
              element={<RouteRequireAuth />}
              handle={{
                crumb: () => ({ to: 'user-settings', name: 'User Settings' }),
              }}
            >
              <Route index element={<UserSettings />} />

              <Route
                path="default-terminal"
                element={<RouteRequireAuth roles={['cashier']} />}
                handle={{ crumb: () => ({ to: 'default-terminal', name: 'Default Terminal' }) }}
              >
                <Route index element={<UserTerminalSelection />} />
              </Route>
            </Route>
          )}

          {
            <Route
              path="pos-pair"
              element={<POSScreenPairing />}
              handle={{
                crumb: () => ({ to: 'POS Pairing', name: 'POS Pairing' }),
              }}
            />
          }

          {
            <Route
              path="pos-transaction"
              element={<POSScreenTransaction />}
              handle={{
                crumb: () => ({ to: 'POS Transaction', name: 'POS Transaction' }),
              }}
            />
          }

          {Features.admin.thirdPartyPayment && (
            <Route
              path="/refunds"
              element={<RouteRequireAuth roles={['refunder']} />}
              handle={{
                crumb: () => ({ to: 'refunds', name: 'Refunds' }),
              }}
            >
              <Route index element={<HtppTransactionDashboard />} />
            </Route>
          )}

          {/* Start of admin routes */}
          {/* Note: All roles defined at this route will also be applied to sub-routes. To allow individual routes to have different roles requirement, remove/reduce the `roles` prop at this route. Then, wrap the sub-routes with their own `<RouteRequireAuth />` and roles prop. */}
          <Route
            path="/admin"
            element={<RouteRequireAuth roles={['admin']} />}
            handle={{
              crumb: () => ({ to: 'admin', name: 'Admin' }),
            }}
          >
            <Route index element={<Admin />} />

            <Route
              path="dynamic-forms"
              element={
                <RouteRequireAdminTileAccess roles={['admin']} tileAccess={'dynamic-form'} />
              }
              handle={{
                crumb: () => ({ to: 'dynamic-forms', name: 'Dynamic Forms' }),
              }}
            >
              <Route index element={<AdminDynamicForms />} />

              <Route
                path="add/:id?"
                element={<AdminDynamicFormsAdd />}
                handle={{
                  crumb: (data) => ({
                    name: `Create Dynamic Form${data.pathname.endsWith('/add') ? '' : ' (Copy)'}`,
                  }),
                }}
              />
              <Route
                path="edit/:id"
                element={<AdminDynamicFormsEdit />}
                handle={{
                  crumb: () => ({ name: 'Edit Dynamic Form' }),
                }}
              />
            </Route>

            {Features.admin.terminalManager && (
              <Route
                path="terminals-manager"
                element={
                  <RouteRequireAdminTileAccess roles={['admin']} tileAccess="terminals-manager" />
                }
                handle={{ crumb: () => ({ to: 'terminals-manager', name: 'Terminal Manager' }) }}
              >
                <Route index element={<UserTerminalsManager />} />

                <Route
                  path="create-terminal/:id?"
                  element={<UserTerminalsForm />}
                  handle={{ crumb: () => ({ to: 'create-terminal', name: 'Create Terminal' }) }}
                />
              </Route>
            )}

            {Features.admin.outageManager && (
              <Route
                path="outage"
                element={<RouteRequireAdminTileAccess roles={['admin']} tileAccess={'outage'} />}
                handle={{
                  crumb: () => ({ to: 'outage', name: 'Outage' }),
                }}
              >
                <Route index element={<AdminOutage />} />

                <Route
                  path="add"
                  element={<AdminOutageEditor />}
                  handle={{
                    crumb: () => ({ name: 'Create Outage' }),
                  }}
                />
                <Route
                  path="edit/:id"
                  element={<AdminOutageEditor />}
                  handle={{
                    crumb: () => ({ name: 'Edit Outage' }),
                  }}
                />
              </Route>
            )}

            {Features.admin.cashierAccess && (
              <Route
                path="cashier-access-manager"
                element={
                  <RouteRequireAdminTileAccess roles={['admin']} tileAccess={'cashier-role'} />
                }
                handle={{
                  crumb: () => ({ to: 'cashier-access-manager', name: 'Cashier Access' }),
                }}
              >
                <Route index element={<AdminCashierAccess />} />

                <Route
                  path="cashier-access"
                  element={<AdminCashierAccessEditor />}
                  handle={{
                    crumb: () => ({ name: 'Create Cashier Access' }),
                  }}
                />
                <Route
                  path="update/:id"
                  element={<AdminCashierAccessEditor />}
                  handle={{
                    crumb: () => ({ name: 'Update Cashier Access' }),
                  }}
                />
              </Route>
            )}

            {Features.admin.ratingReport && (
              <Route
                path="rating-report"
                element={
                  <RouteRequireAdminTileAccess roles={['admin']} tileAccess={'rating-report'} />
                }
                handle={{
                  crumb: () => ({ to: 'rating-report', name: 'Rating Report' }),
                }}
              >
                <Route index element={<AdminCashierAccess />} />
              </Route>
            )}

            {Features.admin.internationalDashboard && (
              <Route
                path="international-access"
                element={
                  <RouteRequireAdminTileAccess
                    roles={['admin']}
                    tileAccess={'international-access'}
                  />
                }
                handle={{
                  crumb: () => ({ to: 'international-access', name: 'International Access' }),
                }}
              >
                <Route index element={<InternationalAccess />} />
                <Route
                  path="edit/:id"
                  element={<InternationalAccessEdit />}
                  handle={{
                    crumb: () => ({ name: 'Edit' }),
                  }}
                />
              </Route>
            )}

            {Features.admin.QRCodeGeneration && (
              <Route
                path="/admin/qr-code-generation"
                element={<RouteRequireAdminTileAccess roles={['admin']} tileAccess={'qr-code'} />}
                handle={{
                  crumb: () => ({ to: 'qr-code-generation', name: 'QR Code Generation' }),
                }}
              >
                <Route index element={<QRCodeGeneration />} />
              </Route>
            )}

            {/* {Features.admin.medicalRecords && (
            <>
              <Route path="/admin/medical-records">
                <Route index element={<AdminMedicalRecords />} />

                <Route path={'fees/:action?'} element={<AdminMedicalRecordFees />} />
                <Route path={'fees/edit/:id?'} element={<AdminMedicalRecordFees />} />

                <Route path={'fulfiller'} element={<AdminMedicalRecordsFulfiller />} />
                <Route path={'fulfiller/view/:id'} element={<AdminMedicalRecordViewRequest />} />
              </Route>
            </>
          )} */}

            {/* {Features.admin.organisations && (
            <>
              <Route path="/admin/organisations">
                <Route index element={<AdminOrganisations />} />

                <Route path={'add-site/:id?'} element={<AdminAddSite />} />
                <Route path={'edit-site/:id'} element={<AdminEditSite />} />

                <Route path="health-entities">
                  <Route index element={<AdminHealthEntities />} />

                  <Route path={'add/:id?'} element={<AdminAddHealthEntity />} />
                  <Route path={'edit/:id'} element={<AdminEditHealthEntity />} />
                </Route>
              </Route>
            </>
          )} */}

            {/* {Features.admin.devTools && (
            <>
              <Route path={'/admin/utilities'} element={<AdminUtilities />} />
            </>
          )} */}
          </Route>
          {/* End of admin routes */}

          <Route
            path="/cashier/error-correction"
            element={<RouteRequireAuth roles={['cashier']} />}
            handle={{
              crumb: () => ({ to: 'error-correction', name: 'Error Correction' }),
            }}
          >
            <Route index element={<InvoiceExceptionHandling />} />)
          </Route>
        </Route>
        {/* End of all normal routes.*/}

        {/* Routes below should have their own layout */}
        {Features.paymentMethods.payto && Features.paymentMethods.payToNonProd && (
          <Route path="/payto-notification" element={<MockPaytoBanking />} />
        )}
      </>,
    ),
  );

  return (
    <Suspense fallback={<ComponentLoading />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
